import React, { useState } from 'react';
import { findCustomerByPhone, markLockerEmpty } from '../data/firebaseService';
import Header from './Header';
import Footer from './Footer';
import VideoModal from './VideoModal';
import './PickupForm.css';

const PickupForm = ({ onBack }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [error, setError] = useState('');
  const [isPickupComplete, setIsPickupComplete] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showPinAfterConfirm, setShowPinAfterConfirm] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const locationNames = {
    'rajinder_nagar': 'Rajinder Nagar',
    'patel_nagar': 'Patel Nagar'
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsSearching(true);
    setError('');
    setCustomerData(null);

    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      setIsSearching(false);
      return;
    }



    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const result = await findCustomerByPhone(phoneNumber);
      
      if (result) {
        // In a real app, you would also verify the order ID
        setCustomerData(result);
      } else {
        setError('No clothes found for this phone number. Please check the number and try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePickup = async () => {
    if (!customerData) return;

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mark locker as empty in the database
      await markLockerEmpty(customerData.location, customerData.lockerId);
      
      // Show PIN after confirmation on the same page
      setShowPinAfterConfirm(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
  };



  if (isPickupComplete) {
    return (
      <div className="pickup-success">
        <Header title="Pickup Successful" />
        <div className="success-container">
          <h2>✅ Pickup Successful!</h2>
          <div className="success-details">
            <p><strong>Phone:</strong> {phoneNumber}</p>
            <p><strong>Locker:</strong> {customerData.lockerId.toUpperCase()}</p>
          </div>
          <div className="success-message">
            <p>🎉 Clothes have been successfully picked up!</p>
            <p>Thank you for using our service.</p>
          </div>
          <button className="back-btn" onClick={onBack}>
            🏠 Back to Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="pickup-form">
      <Header title="Pick Up Clothes" />
      <div className="form-container">
        <div className="form-header">
          {!customerData && (
            <>
              <button className="back-btn" onClick={onBack}>
                ← Back
              </button>
              <h2>🎒 Collect Ready Clothes</h2>
              <p className="location-info whatsapp-notice">Please enter the phone number you have registered with us to receive your locker PIN</p>
            </>
          )}
        </div>

        {!customerData ? (
          <form onSubmit={handleSearch} className="form">
            <div className="form-group">
              <label htmlFor="phoneNumber">📱 Phone Number</label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                required
              />
            </div>



            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={isSearching}
            >
              {isSearching ? '🔍 Searching...' : '🔍 Find Order'}
            </button>
          </form>
        ) : (
          <>
            <div className="pickup-actions">
              <button
                className="pickup-btn"
                onClick={handlePickup}
              >
                ✅ Confirm Pickup
              </button>

              <button
                className="instructions-btn"
                onClick={() => setShowInstructions(true)}
              >
                📋 View Pickup Instructions & Video
              </button>
            </div>
            
            <div className={`customer-details ${!showPinAfterConfirm ? 'blurred' : ''}`}>
              <h3>✅ Order Found!</h3>
              
              <div className={`details-card ${!showPinAfterConfirm ? 'blurred' : ''}`}>
              <div className="detail-row">
                <span className="label">📱 Phone:</span>
                <span className="value">{phoneNumber}</span>
              </div>
              <div className="detail-row">
                <span className="label">📍 Location:</span>
                <span className="value location">{locationNames[customerData.location]}</span>
              </div>
              <div className="detail-row">
                <span className="label">🔒 Locker:</span>
                <span className="value">{customerData.lockerId.toUpperCase()}</span>
              </div>
              <div className="detail-row">
                <span className="label">🔢 PIN Code:</span>
                {!showPinAfterConfirm ? (
                  <span className="value pin-hidden">••••</span>
                ) : (
                  <span className="value pin-revealed">{customerData.pin}</span>
                )}
              </div>
            </div>
            
            {showPinAfterConfirm && (
              <div className="pin-reveal-section">
                <div className="pin-display-card">
                  <div className="pin-highlight">
                    <span className="pin-label">🔢 Your PIN Code:</span>
                    <span className="pin-value">{customerData.pin}</span>
                  </div>
                  <div className="locker-info">
                    <p><strong>📍 Location:</strong> {locationNames[customerData.location]}</p>
                    <p><strong>🔒 Locker:</strong> {customerData.lockerId.toUpperCase()}</p>
                  </div>
                </div>
              </div>
            )}
            </div>
          </>
        )}



        {/* Instructions Lightbox */}
        {showInstructions && (
          <div className="lightbox-overlay" onClick={() => setShowInstructions(false)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <div className="lightbox-header">
                <h3>📋 Pickup Instructions</h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowInstructions(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="instructions-content">
                <div className="instruction-step">
                  <div className="step-number">1</div>
                  <div className="step-text">
                    <strong>Go to your locker:</strong> {customerData?.lockerId.toUpperCase()}
                  </div>
                </div>
                
                <div className="instruction-step">
                  <div className="step-number">2</div>
                  <div className="step-text">
                    <strong>Enter your PIN:</strong> {customerData?.pin}
                  </div>
                </div>
                
                <div className="instruction-step">
                  <div className="step-number">3</div>
                  <div className="step-text">
                    <strong>Collect your clothes</strong> from the locker
                  </div>
                </div>
                
                <div className="instruction-step">
                  <div className="step-number">4</div>
                  <div className="step-text">
                    <strong>Close the locker door</strong> securely
                  </div>
                </div>
                
                <div className="instruction-step">
                  <div className="step-number">5</div>
                  <div className="step-text">
                    <strong>Click "Confirm Pickup"</strong> below to complete the process
                  </div>
                </div>
                
                {/* Video Section */}
                <div className="instruction-video-section">
                  <h4>🎥 Watch Instruction Video</h4>
                  <div className="video-player-container">
                    <video 
                      ref={(el) => {
                        if (el) {
                          el.onplay = () => setIsVideoPlaying(true);
                          el.onpause = () => setIsVideoPlaying(false);
                          el.onended = () => setIsVideoPlaying(false);
                        }
                      }}
                      className="instruction-video-player"
                      controls
                    >
                      <source src="/instruction-video.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>
              
              <div className="lightbox-footer">
                <button 
                  className="confirm-pickup-btn"
                  onClick={() => {
                    setShowInstructions(false);
                    handlePickup();
                  }}
                >
                  ✅ Confirm Pickup
                </button>
              </div>
            </div>
          </div>
        )}
        
        <VideoModal
          isOpen={showVideo}
          onClose={() => setShowVideo(false)}
          videoSrc="/instruction-video.mp4"
          title="How to Use Your PIN Code"
        />
      </div>
      <Footer />
    </div>
  );
};

export default PickupForm; 